import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { iif, Observable, of, Subject, throwError } from 'rxjs';
import { concatMap, delay, map, retryWhen } from 'rxjs/operators';
import { User } from '../_models/user.model';
import { API_URL } from '../../_common/constants';
import { PsiData } from 'src/app/_models/psi-data';
import { CommonService } from 'src/app/_services/common.service';
import { environment } from 'src/environments/environment';
import { ModalService } from 'src/app/_services/modal.service';
import { POLLING_CONFIG } from 'src/app/_common/product-config';
import { StatusCode, CustomInputType, PSIOperators } from '../../_common/_enum/module.enum'
import { FormatInputTypePipe } from '../../_pipes/format-input-type.pipe';

import LogRocket from 'logrocket';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private _currentUser: User = new User();
  private _psiData: PsiData = new PsiData();
  private showAuthenticationScreen: boolean = false;
  private factorChallengeSent: boolean = false;
  private factorVerification: boolean = false;
  private preferredMfaFactor: any = null;

  private userLoggedIn = new Subject<boolean>();
  
  constructor(private http: HttpClient, private commonService: CommonService, private modalService: ModalService, private formatInputTypePipe: FormatInputTypePipe) {
    this.userLoggedIn.next(false);
  }

  get runningServices(){
    return this.commonService.runningServices;
  }

  get header(){​​
    return this.commonService.header;
  }​​

  set header(data){​​
    this.commonService.header = data;
  }​​

  // Login in request for User..
  login(username: string, password: string):Observable<HttpResponse<any>> {
      return this.http.post<any>(API_URL.Login, { username, password }, {observe: 'response'})
        .pipe(map(user => {
            this._psiData = new PsiData();
            this._currentUser = new User();
            this._currentUser.UID = user.body.result.userID;
            this._currentUser.passwordExpirationDate = user.body.result.passwordExpirationDate;
            if(user.body.result.mfaSkipped){
              this._currentUser.mfaSkipped = true;
              this._currentUser.SID = user.body.result.signInResp.sessionID;
              this._currentUser.COOKIE_TOKEN_URL = user.body.result.signInResp.url;
            }else{
              this._currentUser.oktaStateToken = user.body.result.stateToken;
              this._currentUser.oktaSessionToken = user.body.result.sessionToken;
              this._currentUser.tokenExpiration = new Date(user.body.result.tokenExpiresAt);
              this._currentUser.email = user.body.result.email;
              this._currentUser.factors = user.body.result.factors;
              this._currentUser.preferredFactor = user.body.result.preferredFactor;
              this._currentUser.optionalFactor = user.body.result.optionalFactor;
              this._currentUser.oktaTransactionCode = user.body.result.oktaTransactionCode;
              this._currentUser.antiForgeryToken = undefined;
            }

            this._psiData.currentUser = this._currentUser;
            // store user details and basic auth credentials in session storage to keep user logged in between page refreshes
            this.commonService.storeData(this._psiData);
            return user;
        }));
  }

  get authenticationScreen(){
    return this.showAuthenticationScreen;
  }
  set authenticationScreen(value){
    this.showAuthenticationScreen = value;
  }

  get factorChallenged(){
    return this.factorChallengeSent;
  }
  set factorChallenged(value){
    this.factorChallengeSent = value;
  }

  get factorVerified(){
    return this.factorVerification;
  }
  set factorVerified(value){
    this.factorVerification = value;
  }

  get preferredFactor(){
    return this.preferredMfaFactor;
  }
  set preferredFactor(value){
    this.preferredMfaFactor = value;
  }

  //Enroll user in MFA factor of choice
  enrollMFA(requestPayload) {    
    return this.http.post<any>(API_URL.MfaEnroll, requestPayload, {observe: 'response'})
      .pipe(map(response => {
          return response;
      }));
  }

  //Polling to verify Okta Verify QR Code was scanned
  enrollOktaVerify(requestPayload){
    return this.http.post<any>(API_URL.OktaVerifyEnroll, requestPayload, {observe: 'response'})
    .pipe(map((response:any) => {
      if(!response.body.result.enrolled && response.body.result.waiting){
        throw StatusCode.Polling;
      } else if(response.body.result.enrolled && response.body.result.sessionToken){
        this._currentUser = this._psiData.currentUser;
        this._currentUser.oktaSessionToken = response.body.result.sessionToken;
        this._psiData.currentUser = this._currentUser;
        this.commonService.storeData(this._psiData);
      }
      return response.body.result;
    }),
    retryWhen(response => {
        return response.pipe(
          concatMap((error, attempts) => {
            if (error !== StatusCode.Polling) {
                return throwError(error.body.result);
            }
            return iif(
                () => attempts < POLLING_CONFIG.MfaQRMaxPollingAttempt,
                of(error).pipe(delay(POLLING_CONFIG.MfaPollingInterval))
            )
          })
        )
    }))
  }

  //Send Okta Verify push notification to user's mobile device
  sendPush(requestPayload){
    return this.http.post<any>(API_URL.OktaVerifyPush, requestPayload, {observe: 'response'})
      .pipe(map(response => {
        this._currentUser = this._psiData.currentUser;
        this._currentUser.oktaTransactionCode = response.body.result.oktaTransactionCode;
        this._psiData.currentUser = this._currentUser;
        this.commonService.storeData(this._psiData);
          return response;
      }));
  }

  //Polling to verify Okta Verify push notification from user's mobile device
  challengeOktaVerify(requestPayload){
    return this.http.post<any>(API_URL.OktaVerifyChallenge, requestPayload, {observe: 'response'})
    .pipe(map((response:any) => {
      if(!response.body.result.factorVerified && !response.body.result.factorRejected){
        throw StatusCode.Polling;
      } else if(response.body.result.factorVerified) {
        this._currentUser = this._psiData.currentUser;
        this._currentUser.COOKIE_TOKEN_URL = response.body.result.url;
        this._currentUser.SID = response.body.result.sessionID;
        this._psiData.currentUser = this._currentUser;
        this.commonService.storeData(this._psiData);
      }
      return response.body.result;
    }),
    retryWhen(response => {
        return response.pipe(
          concatMap((error, attempts) => {
            if (error !== StatusCode.Polling) {
                return throwError(error.body.result);
            }
            return iif(
              () => attempts < POLLING_CONFIG.MfaMaxPollingAttempt,
              of(error).pipe(delay(POLLING_CONFIG.MfaPollingInterval))
            )
          })
        )
    }))
  }

  //Send MFA One-Time Passcode
  sendChallenge(requestPayload) {    
    return this.http.post<any>(API_URL.MfaChallenge, requestPayload, {observe: 'response'})
      .pipe(map(response => {
          return response;
      }));
  }

  //Verify MFA One-Time Passcode
  verifyChallenge(code){
    return this.http.post<any>(API_URL.VerifyChallenge, code, {observe: 'response'})
      .pipe(map(response => {
          this._currentUser = this._psiData.currentUser;
          this._currentUser.COOKIE_TOKEN_URL = response.body.result.url;
          this._currentUser.SID = response.body.result.sessionID;
          this._psiData.currentUser = this._currentUser;
          this.commonService.storeData(this._psiData);
          return response;
      }));
  }
  
  // Get the Anti-Forgery for all further calls
  antiForgery(): Observable<any>{
    return this.http.get<any>(API_URL.AntiForgery, { observe: 'response' })
      .pipe(map(_token => {
        this._currentUser = this._psiData.currentUser;
        //this._currentUser.antiForgeryToken = _token.headers.get("x-xsrf-token");
        this._currentUser.antiForgeryToken = _token.body.antiForgeryToken;
        this._psiData.currentUser = this._currentUser;
        this.commonService.storeData(this._psiData);
        return _token.body;
      }))
  }

  // Get the permission for Modules listed in Module.enum
  authorize(loginUser):Observable<User> {
    return this.http.get<any>(API_URL.Permission, {observe: 'response'})
    .pipe(map(data=>{
      
      this._currentUser = data.body.result;
      
      this._currentUser.UID = loginUser.UID;
      this._currentUser.SID = loginUser.SID;
      this._currentUser.antiForgeryToken = loginUser.antiForgeryToken;
      this._currentUser.COOKIE_TOKEN_URL = loginUser.COOKIE_TOKEN_URL;
      this._currentUser.passwordExpirationDate = loginUser.passwordExpirationDate;
      
      if(environment.logRocketKey){
        LogRocket.init(environment.logRocketKey, {
          release: environment.version,
          network: {
            requestSanitizer: (request:any) => {
              // if the url contains 'ignore'
              if (request.url.toLowerCase().indexOf('signin') !== -1 || request.url.toLowerCase().indexOf('resetpassword') !== -1) {
                // ignore the request response pair
                return null;
              }
              
              // otherwise log the request normally
              return request;
            },
          },
        });
        
        //LogRocket.identify(loginUser.userId.toString());

        //Set Name and UserId to the LogRocket Session
        LogRocket.identify(this._currentUser.email, {
          name: `${this._currentUser.firstName} ${this._currentUser.lastName}`,
          userName: this._currentUser.email,
          oktaUID: loginUser.UID,
          companyName: this._currentUser.companyName,
          companyId: this._currentUser.companyID,
          userType: this._currentUser.userType,
          userRole: this._currentUser.userRole,
        });
      }

      this._psiData.currentUser = this._currentUser;
      // store user details and basic auth credentials in session storage to keep user logged in between page refreshes
      this.commonService.storeData(this._psiData);

      return this._currentUser;
    }));
  }

  // Forgot password - Request to Reset the password..
  forgot(username: string):Observable<HttpResponse<any>> {
      return this.http.get<any>(API_URL.Forgot + '?emailaddress='+ username, {observe: 'response'})
          .pipe(map(response => {
              console.log("Forgot Password email sent for user " + username);
              return response;
          }));
  }

  // Reset the new password..
  reset(uid: string, newPassword: string, confirmPassword: string):Observable<HttpResponse<any>> {
      return this.http.post<any>(API_URL.Reset, { "code": uid, "newPassword": newPassword, "confirmNewPassword": confirmPassword }, {observe: 'response'})
          .pipe(map(response => {
              console.log("Password has been reset for " + uid);
              return response;
          }));
  }

  // Change the existing password request..
  changePassword(oldPassword: string, newPassword: string, confirmNewPassword: string):Observable<HttpResponse<any>> {
    return this.http.post<any>(API_URL.Change, { "oldPassword": oldPassword, "newPassword": newPassword, "confirmNewPassword": confirmNewPassword }, {observe: 'response'})
        .pipe(map(response => {
          console.log('Password changed!');
          return response;
        }));
  }

  // Get User MFA information for Profile
  mfaProfile():Observable<HttpResponse<any>> {
    return this.http.get<any>(API_URL.MfaProfile, {observe: 'response'})
      .pipe(map(response => {
        this._currentUser = this._psiData.currentUser;
        this._currentUser.factors = response.body.result;
        this._currentUser.mfaPhone = response.body.result.find(factor=> factor.profile).profile.phoneNumber;
        if(this._currentUser.mfaPhone){ this._currentUser.mfaPhone = this.formatInputTypePipe.transform(this._currentUser.mfaPhone.slice(2), {psiCustomInputType: CustomInputType.PHONE, allowBlank: true, maximumFractionDigits: 0}); }
        this._psiData.currentUser = this._currentUser;
        this.commonService.storeData(this._psiData);
        return response.body.result;
    }));
  }

  // Get User information for Profile..
  profile():Observable<HttpResponse<any>> {
    return this.http.get<any>(API_URL.Profile, {observe: 'response'})
      .pipe(map(response => {
        return response.body.result;
    }));
  }

  // Enroll user in MFA factor of choice on update
  updateMfa(requestPayload){
    return this.http.post<any>(API_URL.MfaUpdate, requestPayload, {observe: 'response'})
      .pipe(map(response => {
          return response;
      }));
  }

  // Verify MFA enrollment on update
  updateVerifyMfa(requestPayload){
    return this.http.post<any>(API_URL.UpdateVerifyEnrollment, requestPayload, {observe: 'response'})
    .pipe(map(response => {
        return response;
    }));
  }

  // Send Okta Verify Push on update
  updateSendPush(oktaFactorID){
    return this.http.get<any>(API_URL.UpdateOktaVerifyPush + "/" + oktaFactorID, {observe: 'response'})
    .pipe(map(response => {
      this._currentUser = this._psiData.currentUser;
      this._currentUser.oktaTransactionCode = response.body.result.oktaTransactionCode;
      this._psiData.currentUser = this._currentUser;
      this.commonService.storeData(this._psiData);
      return response;
    }));
  }

  // Poll to verify Okta Push on update
  updateVerifyPush(oktaFactorID, oktaTransactionCode){
    return this.http.get<any>(API_URL.UpdateOktaVerifyEnroll + "/" + oktaFactorID + "?OktaTransactionCode=" + oktaTransactionCode, {observe: 'response'})
    .pipe(map((response:any) => {
      if(!response.body.result.factorVerified && !response.body.result.factorRejected && !response.body.result.expired){
        throw StatusCode.Polling;
      }
      return response.body.result;
    }),
    retryWhen(response => {
        return response.pipe(
          concatMap((error, attempts) => {
            if (error !== StatusCode.Polling) {
                return throwError(error.body.result);
            }
            return iif(
              () => attempts < POLLING_CONFIG.MfaMaxPollingAttempt,
              of(error).pipe(delay(POLLING_CONFIG.MfaPollingInterval))
            )
          })
        )
    }))
  }
  
  // Logout user from their session..
  logout() {
    this.commonService.runningServices.forEach(subscription =>subscription.unsubscribe());
    return this.http.get<any>(API_URL.Logout)
      .pipe(map(response => {
        this.setUserLoggedIn(false);
        console.log('Logged out!');
        this.commonService.removeData();
        this.commonService.removeLocalStorageData();
        this.authenticationScreen = false;
        return response;
    }));
  }

  setUserLoggedIn(userLoggedIn: boolean) {
    this.userLoggedIn.next(userLoggedIn);
    if(!userLoggedIn){ this.authenticationScreen = false; }
  }

  getUserLoggedIn(): Observable<boolean> {
    return this.userLoggedIn.asObservable();
  }
  
  // Get logged in User details..
  get currentUser(): User {
      this._psiData = this.commonService.getData();
      //this._psiData.currentUser = ;//JSON.parse(sessionStorage.getItem('currentUser'));
      if(this._psiData && this._psiData.currentUser){
        return this._psiData.currentUser as User;
      }else{
        return null;
      }
  }
  
  // Get requested Module..
  getModule(moduleName:string):any{
    if(!this._psiData){
      this._psiData = this.commonService.getData();
    }
    if(this._psiData.currentUser.permissions && moduleName in this._psiData.currentUser.permissions){
      return this._psiData.currentUser.permissions[moduleName];
    }else {
      return null;
    }
  }

  // Refresh token
  refreshToken(wantToSetUserLoggedIn = true){
    return this.http.get<any>(API_URL.RefreshToken)
    .pipe(map(response => {
      if(wantToSetUserLoggedIn){
        this.setUserLoggedIn(true);
      }
      console.log('token refreshed!');

      return response;
  }));
  }

  removeData(){
    this.commonService.removeData();
    this.commonService.removeLocalStorageData();
  }

  closeModal() {
    this.modalService.closeModal();
  }

  hasOpenModals() {
    return this.modalService.hasOpenModals();
  }
}
